import {
  PublicEventDetailsResponse,
  PublicExhibitionItem,
} from '@hubs101/booking-api-skd-client/lib/types';
import { Page, Text, View } from '@react-pdf/renderer';

import { getImageSrc } from '@utils/fn';
import { htmlToText } from '@utils/htmlParser';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { globalStyles } from '../../styles';
import { styles } from './styles';
import { SafeImage } from '../../components/SafeImage';
import { truncateText, normalizeText } from '../../../utils';

const chunkArray = <T,>(array: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

interface PartnersAndExhibitorsPageProps {
  event: PublicEventDetailsResponse;
  exhibitions: PublicExhibitionItem[];
  header: JSX.Element;
  footer: JSX.Element;
}

const PartnersAndExhibitorsPage = ({
  event,
  exhibitions,
  header,
  footer,
}: PartnersAndExhibitorsPageProps) => {
  const { t } = useTranslation();

  function extractAndRemoveUrl(htmlString: string) {
    // Regex to match the <a> tag with href and capture the URL
    const urlRegex = /<a[^>]+href="([^"]+)"[^>]*>.*?<\/a><\/p>/i;

    // Extract the URL
    const match = htmlString.match(urlRegex);
    const url = match ? match[1] : null;

    // Remove the entire <p> element that contains the URL
    const cleanedString = htmlString.replace(
      /<p><a[^>]+href="[^"]+"[^>]*>.*?<\/a><\/p>/i,
      ''
    );

    return { url, cleanedString };
  }

  const items = useMemo(
    () =>
      exhibitions
        ?.map(exhibition => {
          const { url, cleanedString } = extractAndRemoveUrl(
            exhibition?.company?.about
          );

          return {
            id: exhibition?.id,
            companyLogo: getImageSrc(exhibition?.company?.logo),
            name: exhibition?.name,
            about: cleanedString,
            website: exhibition?.contact?.website1 || url,
          };
        })
        ?.filter(item => item.companyLogo && item.about) || [],
    [exhibitions]
  );

  const paginatedItems = useMemo(() => {
    if (!items?.length) return [];
    return chunkArray(items, 12);
  }, [items]);

  return (
    <>
      {paginatedItems.map((pageItems, pageIndex) => (
        <Page
          key={pageIndex}
          size="A4"
          orientation="portrait"
          style={styles.page}
        >
          {header}

          <View style={styles.contentWrapper}>
            <Text
              style={{
                ...styles.title,
                color: event?.design?.themeColor,
              }}
            >
              {t('partners-and-exhibitors', {
                year: new Date(event?.start_date).getFullYear(),
              })}
            </Text>

            <View
              style={{
                ...globalStyles.lineBar,
                borderColor: event?.design?.themeColor ?? 'black',
                marginLeft: 0,
                marginBottom: 20,
                width: 89,
              }}
            />

            <View style={styles.exhibitionWrapper}>
              {pageItems.map(exhibition => (
                <View
                  style={styles.exhibitionItem}
                  wrap={false}
                  key={exhibition.id}
                >
                  <View style={styles.row} wrap={false}>
                    <View style={styles.imageWrapper}>
                      <SafeImage
                        style={styles.companyLogo}
                        src={exhibition.companyLogo}
                        debugLabel={`business-partner-logo-${exhibition.id}`}
                      />
                    </View>

                    <View
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        height: '100%',
                        position: 'relative',
                      }}
                    >
                      <Text style={styles.aboutText}>
                        {truncateText(
                          normalizeText(htmlToText(exhibition.about)),
                          588
                        )}
                      </Text>

                      <Text
                        style={{
                          ...styles.websiteURLText,
                          bottom: 0,
                          position: 'absolute',
                        }}
                      >
                        {exhibition.website?.length ? exhibition.website : ''}
                      </Text>
                    </View>
                  </View>
                </View>
              ))}
            </View>
          </View>

          {footer}
        </Page>
      ))}
    </>
  );
};

export default PartnersAndExhibitorsPage;
